import { useState, useEffect, useCallback } from "react";
import { FaSave, FaPrint, FaTrash, FaMoneyBillWave, FaUserMd } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { HiXMark } from "react-icons/hi2";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import { NicuPrintLayout } from "../../NicuPrintLayout";
import DynamicSelect from "../../DynamicSelect";

const NicuAddPaymentModal = ({
  isOpen,
  onClose,
  bill: initialBill,
  onPaymentSuccess,
}) => {
  const [billDetails, setBillDetails] = useState(initialBill);
  const [transactions, setTransactions] = useState(initialBill?.payments || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  
  const [confirmDelete, setConfirmDelete] = useState({ show: false, paymentId: null });
  const [printData, setPrintData] = useState(null);

 const [patientForm, setPatientForm] = useState({
    date: new Date().toISOString().split("T")[0],
    amount: "",
    discount: "",
    discountBy: "",
    paymentMode: "Cash",
    note: "",
  });
  const [discountUsers, setDiscountUsers] = useState([]);

  const [doctorForm, setDoctorForm] = useState({
    doctorId: null,
    amount: "",
    note: "",
    date: new Date().toISOString().split("T")[0],
  });

  const numberToWordsBD = (value) => {
  let num = Math.floor(Number(value) || 0);
  if (num === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : ""));
  const three = (n) => { const h = Math.floor(n / 100); const r = n % 100; return (h ? ones[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : ""); };
  let words = "";
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  if (crore) words += three(crore) + " Crore ";
  if (lakh) words += two(lakh) + " Lakh ";
  if (thousand) words += two(thousand) + " Thousand ";
  if (num) words += three(num);
  return words.trim();
};
 const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
 const BASE_URL = "https://api.muktihospital.com/api";

  // Load discount users for the "Discount By" dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BASE_URL}/nicu/discount-user`, { headers: { "x-api-key": API_KEY } });
        setDiscountUsers(res.data.users || []);
      } catch (e) { console.error("Failed to load discount users:", e); }
    })();
  }, []);

  useEffect(() => {
    if (billDetails?.discountBy) {
      setPatientForm((prev) => ({ ...prev, discountBy: billDetails.discountBy }));
    }
  }, [billDetails?.discountBy]);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/doctor`, { headers: { "x-api-key": API_KEY } });
        let rawData = [];
        if (Array.isArray(res.data)) rawData = res.data;
        else if (res.data?.doctors) rawData = res.data.doctors;
        else if (res.data?.data) rawData = res.data.data;

        const options = rawData.map(d => {
             let name = d.name || "Unknown";
             if(d.translations) {
                 try {
                     const t = typeof d.translations === 'string' ? JSON.parse(d.translations) : d.translations;
                     name = t?.en?.name || t?.bn?.name || name;
                 } catch(e){}
             }
             return { value: d.id, label: name + (d.specialization ? ` (${d.specialization})` : '') };
        });
        setDoctors(options);
      } catch (error) { console.error(error); }
    };
    if (isOpen) fetchDoctors();
  }, [isOpen]);

  const fetchLatestData = useCallback(async () => {
    if (!initialBill?.id) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/nicu/nicu-bills/${initialBill.id}`, {
        headers: { "x-api-key": API_KEY },
      });
      const fullData = res.data.bill;
      setBillDetails(fullData);
      setTransactions(fullData.payments || []);
      const due = (fullData.netAmount || 0) - (fullData.paidAmount || 0);
      setPatientForm(prev => ({ ...prev, amount: due > 0 ? due : "" }));
    } catch (error) { console.error(error); } 
    finally { setIsLoading(false); }
  }, [initialBill?.id]);

  useEffect(() => {
    if (isOpen) {
        setBillDetails(initialBill);
        setTransactions(initialBill?.payments || []);
        fetchLatestData();
    }
  }, [isOpen, initialBill, fetchLatestData]);

  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    const remainingDue = (billDetails?.netAmount || 0) - (billDetails?.paidAmount || 0);
    if (parseFloat(patientForm.amount || 0) > remainingDue && remainingDue > 0) {
         toast.warning("Warning: Payment exceeds due amount.");
    }
    
    setIsSubmitting(true);
    try {
      const payload = {
        billId: billDetails.id,
        paymentDate: patientForm.date,
        amount: parseFloat(patientForm.amount || 0),
        discount: parseFloat(patientForm.discount || 0),
        discountBy: patientForm.discountBy || null,
        paymentMode: patientForm.paymentMode,
        note: patientForm.note,
        doctorId: null,
        doctorAmount: null,
        doctorNote: null
      };

      const res = await axios.post(`${BASE_URL}/nicu/nicu-bills/add-payment`, payload, { headers: { "x-api-key": API_KEY } });
      toast.success("Patient payment received!");

      if (res.data?.autoReleased) {
        toast.success("All bills cleared — patient released and the bed is now free.", { autoClose: 5000 });
      }

      if (onPaymentSuccess) onPaymentSuccess();
      await fetchLatestData();

      setPatientForm(prev => ({ ...prev, amount: "", discount: "", note: "" }));
    } catch (error) {
      toast.error("Failed to save patient payment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDoctorSubmit = async (e) => {
    e.preventDefault();
    if (!doctorForm.doctorId || !doctorForm.amount) {
        return toast.warning("Please select a doctor and amount.");
    }

    setIsSubmitting(true);
    try {
      const payload = {
        billId: billDetails.id,
        paymentDate: doctorForm.date,
        amount: 0,
        discount: 0, 
        paymentMode: "Cash", 
        note: "Doctor Payment", 
        doctorId: doctorForm.doctorId,
        doctorAmount: parseFloat(doctorForm.amount),
        doctorNote: doctorForm.note
      };

      await axios.post(`${BASE_URL}/nicu/nicu-bills/add-payment`, payload, { headers: { "x-api-key": API_KEY } });
      toast.success("Doctor paid successfully!");
      await fetchLatestData();
      setDoctorForm(prev => ({ ...prev, amount: "", note: "" })); 
    } catch (error) {
      toast.error("Failed to pay doctor.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Print Logic
  useEffect(() => {
    if (printData) {
      const timer = setTimeout(() => {
        const printLayout = document.querySelector(".payment-history-print");
        if (printLayout) {
          printLayout.style.display = "block";
          window.print();
          setTimeout(() => {
            printLayout.style.display = "none";
            setPrintData(null);
          }, 500);
        } else {
           setPrintData(null);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [printData]);

const initiateDelete = (paymentId) => setConfirmDelete({ show: true, paymentId });
  const executeDelete = async () => {
    if (!confirmDelete.paymentId) return;
    try {
      await axios.delete(`${BASE_URL}/nicu/nicu-bills/delete-payment/${confirmDelete.paymentId}`, {
         headers: { "x-api-key": API_KEY } 
      });
      toast.success("Transaction deleted!");
      if (onPaymentSuccess) onPaymentSuccess();
      await fetchLatestData();
      setConfirmDelete({ show: false, paymentId: null });
    } catch (error) {
      toast.error("Failed to delete transaction.");
    }
  };

  const handlePrintReceipt = (transaction) => {
    const activeBill = billDetails || initialBill || {};
    const isDocPay = transaction.doctorAmount > 0;
    // the ONLY difference from the bill copy: the paid figure is this transaction's amount
    const thisPayment = parseFloat(isDocPay ? transaction.doctorAmount : transaction.amount) || 0;

    setPrintData({
      title: isDocPay ? "Doctor Payment Voucher" : "NICU Bill",
      billInfo: (
        <div style={{ border: "1px solid #000", padding: "5px 8px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", columnGap: "14px", rowGap: "1px" }}>
            <p style={{ margin: 0 }}><strong>Bill No:</strong> {activeBill.billNumber || "N/A"}</p>
            <p style={{ margin: 0 }}><strong>Reg No:</strong> {activeBill.regNo || "N/A"}</p>
            <p style={{ margin: 0 }}><strong>Special No:</strong> {activeBill.specialNo || "N/A"}</p>

            <p style={{ margin: 0 }}><strong>Patient:</strong> {activeBill.patientName || "N/A"}</p>
            <p style={{ margin: 0 }}><strong>Age / Sex:</strong> {activeBill.age || "--"} / {activeBill.gender || "--"}</p>
            <p style={{ margin: 0 }}><strong>Bed No:</strong> {activeBill.bedNo || "--"}</p>

            <p style={{ margin: 0 }}><strong>Admit Date:</strong> {activeBill.admissionDate ? new Date(activeBill.admissionDate).toLocaleDateString("en-GB") : "N/A"}</p>
            <p style={{ margin: 0 }}><strong>Payment Date:</strong> {new Date(transaction.paymentDate || transaction.createdAt).toLocaleDateString("en-GB")}</p>
            <p style={{ margin: 0 }}><strong>Mode:</strong> {transaction.paymentMode || "Cash"}</p>

            <p style={{ margin: 0, gridColumn: "1 / -1" }}><strong>Consultant:</strong> {activeBill.consultantName || "N/A"}</p>
          </div>
        </div>
      ),
      table: (
        <>
          <thead>
            <tr>
              <th style={{ width: "8%" }}>Sl</th>
              <th>Service Name</th>
              <th style={{ width: "16%" }}>Unit Price</th>
              <th style={{ width: "12%" }}>Qty</th>
              <th style={{ width: "18%" }}>Amount (৳)</th>
            </tr>
          </thead>
          <tbody>
            {(activeBill.serviceItems || []).map((item, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{item.serviceName || "N/A"}{item.particulars ? ` — ${item.particulars}` : ""}</td>
                <td>{parseFloat(item.unitPrice || 0).toFixed(2)}</td>
                <td>{item.totalUnit || "0"}</td>
                <td>{parseFloat(item.amount || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </>
      ),
      summary: (
        <>
          <div><span>Bill Amount</span><span>৳ {parseFloat(activeBill.billAmount || 0).toFixed(2)}</span></div>
          <div>
            <span>Service Charge{parseFloat(activeBill.serviceChargePc) > 0 ? ` (${parseFloat(activeBill.serviceChargePc)}%)` : ""}</span>
            <span>৳ {parseFloat(activeBill.serviceCharge || 0).toFixed(2)}</span>
          </div>
          <div><span>Discount</span><span>৳ {parseFloat(activeBill.discount || 0).toFixed(2)}</span></div>
          <div><span>Net Amount</span><span>৳ {parseFloat(activeBill.netAmount || 0).toFixed(2)}</span></div>
          <div><span>Paid Amount</span><span>৳ {thisPayment.toFixed(2)}</span></div>
          <div><span>Payable (Due)</span><span>৳ {parseFloat(activeBill.payableAmount || 0).toFixed(2)}</span></div>
        </>
      ),
      isPaid: (activeBill.payableAmount || 0) <= 0,
      footer: (
        <>
          <div className="np-sign-item">
            <div className="np-sign-space" /><div className="np-sign-line" />
            <p className="np-sign-label">Received By</p>
          </div>
          <div className="np-sign-item">
            <div className="np-sign-space" /><div className="np-sign-line" />
             <p className="np-sign-label">Authorized By</p>
          </div>
          <div className="np-sign-item">
            <div className="np-sign-space" /><div className="np-sign-line" />
            <p className="np-sign-label">Accounts Officer</p>
          </div>
        </>
      ),
    });
  };

  if (!isOpen) return null;

  const activeBill = billDetails || initialBill || {};
  const patient = activeBill.patient || {};
  const currentTotalAmount = activeBill.totalAmount || 0;
  const currentPaid = activeBill.paidAmount || 0;
  const currentDue = (activeBill.netAmount || 0) - currentPaid;

  const InfoRow = ({ label, value, isBold }) => (
    <div className="flex justify-between border-b border-gray-100 py-2 text-sm last:border-0">
      <span className="text-gray-500 font-medium">{label}</span>
      <span className={`text-right text-gray-800 ${isBold ? "font-bold" : ""}`}>{value || "-"}</span>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 overflow-y-auto print-hidden">
        <div className="bg-white shadow-lg w-full my-4 sm:my-0 max-h-[100vh] h-full overflow-y-auto">
          
          {/* Header */}
          <div className="flex items-center justify-between bg-gray-200 p-4 border-b border-gray-300">
             <h2 className="text-lg font-bold">Manage Payments</h2>
             <button onClick={onClose} className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"><HiXMark/></button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              
              {/* LEFT: Bill Details */}
              <div className="lg:col-span-4 space-y-4">
                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-gray-700 font-bold border-b pb-2 mb-3 text-sm uppercase">Bill Summary</h3>
                  <InfoRow label="Bill No" value={activeBill.billNumber} isBold />
                  <InfoRow label="Total Bill" value={`৳ ${currentTotalAmount}`} />
                  <InfoRow 
                    label="Total Discount" 
                    value={`৳ ${(activeBill.discount || 0).toLocaleString()}`} 
                    className="text-blue-600" 
                  />
                  <InfoRow label="Total Paid" value={`৳ ${currentPaid}`} isBold/>
                  <div className="flex justify-between py-2 text-sm font-bold text-red-600 border-t mt-2">
                      <span>Due Amount</span>
                      <span>৳ {currentDue}</span>
                  </div>
                </div>
                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-gray-700 font-bold border-b pb-2 mb-3 text-sm uppercase">Patient Information</h3>
                  <InfoRow label="Patient Name" value={activeBill.patientName} />
                  <InfoRow label="Reg No" value={activeBill.regNo} />
                  <InfoRow label="Age" value={activeBill.age || patient.age || "N/A"} />
                  
                  {/* Added Fields */}
                  <InfoRow label="Gender" value={patient.gender || activeBill.gender} />
                  <InfoRow label="Blood Group" value={patient.bloodGroup || activeBill.bloodGroup} />
                  
                  <InfoRow label="Mobile" value={patient.phoneNumber || activeBill.phoneNumber} />
                  
                  {/* Added Email */}
                  <InfoRow label="Email" value={patient.email || activeBill.email} />
                  
                  <InfoRow label="Address" value={patient.address || activeBill.address} />
                </div>
              </div>

              {/* RIGHT: TWO FORMS */}
              <div className="lg:col-span-8 space-y-6">
                
                {/* 1. PATIENT PAYMENT (GREEN) */}
                <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-[#73CB64]">
                   <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                       <FaMoneyBillWave className="text-[#73CB64]"/> Receive Patient Payment
                   </h3>
                   <form onSubmit={handlePatientSubmit}>
                       <div className="grid grid-cols-2 gap-3 mb-3">
                           <div>
                               <label className="text-xs font-bold block mb-1">Date</label>
                               <input type="date" value={patientForm.date} onChange={e=>setPatientForm({...patientForm, date: e.target.value})} className="w-full border p-2 rounded text-sm"/>
                           </div>
                           <div>
                               <label className="text-xs font-bold block mb-1">Amount (৳)</label>
                               <input
                                 type="number"
                                 placeholder="0.00"
                                 value={patientForm.amount}
                                 onChange={(e) => {
                                   const amt = parseFloat(e.target.value || 0);
                                   const due = (activeBill.netAmount || 0) - (activeBill.paidAmount || 0);
                                   let autoDiscount = due - amt;
                                   if (autoDiscount < 0) autoDiscount = 0;
                                   setPatientForm({
                                     ...patientForm,
                                     amount: e.target.value,
                                     discount: e.target.value === "" ? "" : String(Math.round(autoDiscount)),
                                   });
                                 }}
                                 className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none"
                               />
                           </div>
                       </div>
                       <div className="grid grid-cols-2 gap-3 mb-3">
                           <div>
                               <label className="text-xs font-bold block mb-1">Discount (৳)</label>
                               <input type="number" placeholder="0.00" value={patientForm.discount} onChange={e=>setPatientForm({...patientForm, discount: e.target.value})} className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none"/>
                           </div>
                           <div>
                               <label className="text-xs font-bold block mb-1">Discount By</label>
                               <DynamicSelect
                                 options={discountUsers.map((u) => ({ value: u.userName, label: u.userName }))}
                                 value={patientForm.discountBy ? { value: patientForm.discountBy, label: patientForm.discountBy } : null}
                                 onChange={(opt) => setPatientForm({ ...patientForm, discountBy: opt ? opt.value : "" })}
                                 isSearchable
                                 placeholder="Select who gives discount"
                               />
                           </div>
                       </div>
                       <div className="flex gap-3 mb-3">
                           <div className="flex-1">
                               <label className="text-xs font-bold block mb-1">Mode</label>
                               <select value={patientForm.paymentMode} onChange={e=>setPatientForm({...patientForm, paymentMode: e.target.value})} className="w-full border p-2 rounded text-sm">
                                   <option>Cash</option><option>Card</option><option>Mobile Banking</option>
                               </select>
                           </div>
                           <div className="flex-[2]">
                               <label className="text-xs font-bold block mb-1">Note</label>
                               <input type="text" value={patientForm.note} onChange={e=>setPatientForm({...patientForm, note: e.target.value})} className="w-full border p-2 rounded text-sm"/>
                           </div>
                       </div>
                       <div className="text-right">
                           <button type="submit" disabled={isSubmitting} className="bg-[#73CB64] text-white px-5 py-2 rounded text-sm hover:bg-M-primary-color font-medium shadow-sm transition">
                              {isSubmitting ? "..." : "Receive Payment"}
                           </button>
                       </div>
                   </form>
                </div>

                {/* 2. DOCTOR PAYMENT (PURPLE) */}
                <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-M-primary-color/90">
                   <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                       <FaUserMd className="text-M-primary-color/90"/> Pay Doctor (Commission/Fee)
                   </h3>
                   <form onSubmit={handleDoctorSubmit}>
                       <div className="mb-3">
                           <label className="text-xs font-bold block mb-1">Select Doctor</label>
                           <DynamicSelect 
                               options={doctors}
                               value={doctors.find(d => d.value === doctorForm.doctorId)}
                               onChange={(opt) => setDoctorForm({...doctorForm, doctorId: opt?.value || null})}
                               placeholder="Search Doctor..."
                               isClearable
                           />
                       </div>
                       <div className="grid grid-cols-2 gap-3 mb-3">
                           <div>
                               <label className="text-xs font-bold block mb-1">Pay Amount (৳)</label>
                               <input type="number" placeholder="0.00" value={doctorForm.amount} onChange={e=>setDoctorForm({...doctorForm, amount: e.target.value})} className="w-full border p-2 rounded text-sm focus:border-M-primary-color/90 outline-none"/>
                           </div>
                           <div>
                               <label className="text-xs font-bold block mb-1">Reason / Note</label>
                               <input type="text" placeholder="e.g. Commission" value={doctorForm.note} onChange={e=>setDoctorForm({...doctorForm, note: e.target.value})} className="w-full border p-2 rounded text-sm"/>
                           </div>
                       </div>
                       <div className="text-right">
                           <button type="submit" disabled={isSubmitting || !doctorForm.doctorId} className="bg-M-primary-color/90 text-white px-5 py-2 rounded text-sm hover:bg-M-primary-color font-medium shadow-sm transition disabled:opacity-50">
                               {isSubmitting ? "..." : "Pay Doctor"}
                           </button>
                       </div>
                   </form>
                </div>

              </div>
            </div>

            {/* TRANSACTION HISTORY TABLE */}
            <div className="mt-8 bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-gray-800 font-bold border-b pb-3 mb-4 text-sm uppercase">Transaction History</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border border-gray-200">
                  <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3 font-semibold border-b">ID</th>
                      <th className="px-4 py-3 font-semibold border-b">Date</th>
                      <th className="px-4 py-3 font-semibold border-b">Details</th>
                      <th className="px-4 py-3 font-semibold border-b text-right">Amount (৳)</th>
                      <th className="px-4 py-3 font-semibold border-b text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-gray-700">
                    {transactions.length > 0 ? transactions.map((tx, idx) => {
                       const isDocPay = tx.doctorAmount > 0;
                       
                       let docName = "Doctor";
                       if(tx.doctor?.translations) {
                            try {
                                const t = typeof tx.doctor.translations === 'string' ? JSON.parse(tx.doctor.translations) : tx.doctor.translations;
                                docName = t?.en?.name || docName;
                            } catch(e){}
                       } else if (tx.doctor?.name) {
                            docName = tx.doctor.name;
                       }

                       return (
                          <tr key={idx} className={`hover:bg-gray-50 ${isDocPay ? 'bg-purple-50/40' : ''}`}>
                            <td className="px-4 py-3 font-mono text-xs text-gray-500">{tx.id ? `PAY-${tx.id}` : '-'}</td>
                            <td className="px-4 py-3 whitespace-nowrap">{new Date(tx.paymentDate).toLocaleDateString("en-GB")}</td>
                            
                            {/* DETAILS COLUMN */}
                            <td className="px-4 py-3">
                                {isDocPay ? (
                                    <div className="flex flex-col">
                                        <span className="text-xs font-bold text-M-primary-color/90 flex items-center gap-1">
                                            <FaUserMd size={10}/> Paid to: {docName}
                                        </span>
                                        {tx.doctorNote && <span className="text-[10px] text-gray-500 italic mt-0.5">{tx.doctorNote}</span>}
                                    </div>
                                ) : (
                                    <div className="flex flex-col">
                                        <span className="text-xs font-bold text-[#73CB64] flex items-center gap-1">
                                            <FaMoneyBillWave size={10}/> Received from Patient
                                        </span>
                                        <span className="text-[10px] text-gray-500 mt-0.5">Via {tx.paymentMode} {tx.note ? `- ${tx.note}` : ''}</span>
                                    </div>
                                )}
                            </td>

                            {/* AMOUNT COLUMN */}
                            <td className="px-4 py-3 text-right font-bold text-sm">
                                {isDocPay ? (
                                    <span className="text-red-500">- {parseFloat(tx.doctorAmount).toFixed(2)}</span>
                                ) : (
                                    <span className="text-[#73CB64]">+ {parseFloat(tx.amount).toFixed(2)}</span>
                                )}
                            </td>

                            <td className="px-4 py-3">
                              <div className="flex justify-center gap-2">
                                <button onClick={() => handlePrintReceipt(tx)} className="text-blue-500 hover:text-blue-700 transition" title="Print"><FaPrint /></button>
                                <button onClick={() => initiateDelete(tx.id)} className="text-red-500 hover:text-red-700 transition" title="Delete"><FaTrash /></button>
                              </div>
                            </td>
                          </tr>
                       );
                    }) : (
                      <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-400 italic">No transactions found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
      </div>
      
      {/* Print Layout */}
      {printData && (
        <NicuPrintLayout
            className="payment-history-print"
            title={printData.title}
            compact
            billInfo={printData.billInfo}
            table={printData.table}
            summary={printData.summary}
            footer={printData.footer}
            isPaid={printData.isPaid}
        />
      )}

      {/* Confirm Modal */}
      <ConfirmActionModal
        isOpen={confirmDelete.show}
        onClose={() => setConfirmDelete({ show: false, paymentId: null })}
        onConfirm={executeDelete}
        ids={[confirmDelete.paymentId]}
        entityName="payment"
        title="Delete Payment"
        message="Are you sure you want to delete this payment record? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default NicuAddPaymentModal;